import { Box, TextField, InputAdornment } from "@mui/material";
import { Download, Search } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { CrudSort, useCustom } from "@refinedev/core";
import { useEffect, useMemo, useState, ReactNode } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useMarketplaceStore } from "../store/useMarketplaceStore";
import { useFiltersWithContext } from "./dataGridFilter/filter-context/useFiltersWithContext";
import { getQueryFromFilter } from "./dataGridFilter/getQueryFromFilter";

type BaseListFilterRowProps = {
  placeholder: string;
  sorters: CrudSort[];
  exportUrl: string;
  rightActions?: ReactNode;
  showFiltersButton?: ReactNode;
};

export const BaseListFilterRow = ({
  placeholder,
  sorters,
  exportUrl,
  rightActions,
  showFiltersButton,
}: BaseListFilterRowProps) => {
  const { translate } = useTranslation();
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);

  const { getFilterValue, searchDebounced, filters } = useFiltersWithContext();

  const search = getFilterValue("searchTerm", "eq");
  const [localSearch, setLocalSearch] = useState<string | null>(search);

  useEffect(() => {
    setLocalSearch(search ?? null);
  }, [search]);

  useEffect(() => {
    searchDebounced(localSearch?.trim() || null);
  }, [localSearch, searchDebounced]);

  const query = useMemo(
    () => [
      ...getQueryFromFilter(filters),
      { field: "sort", value: sorters[0]?.field },
      { field: "order", value: sorters[0]?.order },
      { field: "extended", value: true },
    ],
    [filters, sorters],
  );

  const { refetch, isFetching } = useCustom({
    url: `store/${selectedCountry?.id}/${exportUrl}`,
    method: "get",
    config: { query },
    queryOptions: { enabled: false, staleTime: 0 },
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        margin: "8px 0",
        padding: "8px 16px",
        width: "100%",
        backgroundColor: (theme) => theme.palette.customBackground.box,
      }}
    >
      <TextField
        placeholder={placeholder}
        size="small"
        value={localSearch ?? ""}
        onChange={(e) => setLocalSearch(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          },
        }}
      />

      <Box flex={1} />

      {rightActions}

      <LoadingButton
        loading={isFetching}
        onClick={() => refetch()}
        variant="contained"
        startIcon={<Download />}
      >
        {translate("buttons.download")}
      </LoadingButton>

      {showFiltersButton}
    </Box>
  );
};
